import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GeneralService } from 'src/app/services/general/general.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';

declare var bootstrap: any;

@Component({
  selector: 'app-vehicle-upload-documents',
  templateUrl: './vehicle-upload-documents.component.html',
  styleUrls: ['./vehicle-upload-documents.component.scss']
})
export class VehicleUploadDocumentsComponent extends DestructorComponent implements OnInit {

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Output() documentsUploaded = new EventEmitter<void>();

  documentForm: FormGroup;
  selectedVehicle: any = null;
  selectedFiles: File[] = [];
  isUploading: boolean = false;
  formSubmitted: boolean = false;
  uploadProgress: { current: number; total: number } = { current: 0, total: 0 };
  currentUser: any = null;

  // All document types
  private allDocumentTypes = [
    { value: 'accessories_sheet', label: 'Accessories Sheet' },
    { value: 'auction_bill', label: 'Auction Bill' },
    { value: 'auction_sheet', label: 'Auction Sheet' },
    { value: 'bl_surrender', label: 'B/L Surrender' },
    { value: 'bank_docs', label: 'Bank Documents' },
    { value: 'bl_amend_1', label: 'BL Ammend 1' },
    { value: 'bl_amend_2', label: 'BL Ammend 2' },
    { value: 'bl_amend_3', label: 'BL Ammend 3' },
    { value: 'bl_copy', label: 'BL Copy' },
    { value: 'bl_final', label: 'BL Final' },
    { value: 'domestic_transport', label: 'Domestic Transport' },
    { value: 'export_certificate', label: 'Export Certificate' },
    { value: 'export_certificate_ext', label: 'Export Certificate (ext)' },
    { value: 'export_certificate_trans', label: 'Export Certificate (Trans)' },
    { value: 'export_declaration', label: 'Export Declaration' },
    { value: 'freight_memo', label: 'Freight Memo' },
    { value: 'initial_docs', label: 'Initial Docs' },
    { value: 'inspection_certificate', label: 'Inspection Certificate' },
    { value: 'lc', label: 'LC' },
    { value: 'lc_document', label: 'LC Document' },
    { value: 'bl_docs', label: 'BL Documents' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'insurance', label: 'Insurance Certificate' },
    { value: 'inspection_report', label: 'Inspection Report' },
    { value: 'registration', label: 'Registration Documents' },
    { value: 'customs_clearance', label: 'Customs Clearance' },
    { value: 'shipping_documents', label: 'Shipping Documents' },
    { value: 'other', label: 'Other Documents' }
  ];

  // Filtered document types based on permissions
  documentTypes: any[] = [];

  constructor(
    private fb: FormBuilder,
    private _generalService: GeneralService,
    private _vehicleService: VehicleService,
    private _globalStateService: GlobalStateService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    // Subscribe to user to get permissions
    this.subscriptions.push(
      this._globalStateService.user.subscribe(user => {
        this.currentUser = user;
        this.filterDocumentTypesByPermissions();
      })
    );

    // Listen for modal show event to get selected vehicle
    const modalElement = document.getElementById('kt_modal_vehicle_upload_documents');
    if (modalElement) {
      modalElement.addEventListener('show.bs.modal', () => {
        const selectedVehicle = (window as any).selectedVehicleForDocuments;
        if (selectedVehicle) {
          this.setSelectedVehicle(selectedVehicle);
        }
      });
    }
  }

  /**
   * Convert document type value from underscore format to hyphen format
   * Example: accessories_sheet -> accessories-sheet
   */
  private convertToPermissionKey(documentTypeValue: string): string {
    return documentTypeValue.replace(/_/g, '-');
  }

  /**
   * Filter document types based on user permissions
   */
  private filterDocumentTypesByPermissions(): void {
    if (!this.currentUser) {
      this.documentTypes = [];
      return;
    }

    // If user is admin, show all document types
    if (this.currentUser?.role_complete?.is_admin) {
      this.documentTypes = [...this.allDocumentTypes];
      return;
    }

    // Filter based on upload-documents permissions
    const uploadDocsPermissions = this.currentUser?.role_complete?.permissions?.['upload-documents'];

    if (!uploadDocsPermissions) {
      this.documentTypes = [];
      return;
    }

    this.documentTypes = this.allDocumentTypes.filter(docType => {
      const permissionKey = this.convertToPermissionKey(docType.value);
      return uploadDocsPermissions[permissionKey] === true;
    });
  }

  /**
   * Check if user has permission to view a specific document type
   */
  canViewDocumentType(documentTypeValue: string): boolean {
    if (!this.currentUser) {
      return false;
    }

    // Admin can view all
    if (this.currentUser?.role_complete?.is_admin) {
      return true;
    }

    const uploadDocsPermissions = this.currentUser?.role_complete?.permissions?.['upload-documents'];
    if (!uploadDocsPermissions) {
      return false;
    }

    const permissionKey = this.convertToPermissionKey(documentTypeValue);
    return uploadDocsPermissions[permissionKey] === true;
  }

  private initializeForm(): void {
    this.documentForm = this.fb.group({
      documentType: ['', [Validators.required]]
    });
  }

  setSelectedVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    // Initialize docs array if it doesn't exist
    if (!this.selectedVehicle.docs) {
      this.selectedVehicle.docs = [];
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFiles = Array.from(input.files);
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async onSubmit(): Promise<void> {
    this.formSubmitted = true;

    if (this.documentForm.invalid || this.selectedFiles.length === 0) {
      return;
    }

    this.isUploading = true;
    this.uploadProgress = { current: 0, total: this.selectedFiles.length };

    try {
      const documentType = this.documentForm.get('documentType')?.value;
      const uploadedUrls: string[] = [];

      // Upload all files first
      for (let i = 0; i < this.selectedFiles.length; i++) {
        const file = this.selectedFiles[i];
        this.uploadProgress.current = i + 1;

        const fileUrl = await this.uploadSingleFile(file);
        uploadedUrls.push(fileUrl);
      }

      // Prepare docs data structure: docs array with { type: type, urls: [] }
      const updatedDocs = [...this.selectedVehicle.docs];

      // Find if this document type already exists
      const existingDocIndex = updatedDocs.findIndex(doc => doc.type === documentType);

      if (existingDocIndex !== -1) {
        // Add to existing document type URLs
        updatedDocs[existingDocIndex].urls = [...updatedDocs[existingDocIndex].urls, ...uploadedUrls];
      } else {
        // Create new document type entry
        updatedDocs.push({
          type: documentType,
          urls: uploadedUrls
        });
      }

      // Update vehicle using VehicleService UpdateItems
      const updateData = {
        docs: updatedDocs
      };

      this.subscriptions.push(
        this._vehicleService.UpdateItems(this.selectedVehicle._id, updateData).subscribe({
          next: (response: any) => {
            if (response.status) {
              // Update local vehicle data
              this.selectedVehicle.docs = updatedDocs;

              // Log form values as requested
              console.log('Document upload form values:', {
                vehicleId: this.selectedVehicle._id,
                documentType: documentType,
                documentsUploaded: this.selectedFiles.length,
                docs: updatedDocs
              });

              // Reset form and files
              this.resetForm();
              this.isUploading = false;


              // Emit event to reload vehicle listing
              this.documentsUploaded.emit();

              // Close modal
              this.closeModal();

              // Show success message
              console.log('Documents uploaded successfully!');
            } else {
              throw new Error(response.message || 'Failed to update vehicle documents');
            }
          },
          error: (error) => {
            this.isUploading = false;
            console.error('Error updating vehicle documents:', error);
            throw error;
          }
        })
      );

    } catch (error) {
      this.isUploading = false;
      console.error('Error uploading documents:', error);
      // Handle error (you can add toast notification here)
    } finally {
      this.uploadProgress = { current: 0, total: 0 };
    }
  }

  private uploadSingleFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
        next: (res) => {
          const fileUrl = res.data.url;
          const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

          this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
            next: () => {
              resolve(fileUrl);
            },
            error: (error) => {
              reject(error);
            }
          });
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  getDocumentTypeLabel(value: string): string {
    const docType = this.documentTypes.find(type => type.value === value);
    return docType ? docType.label : value;
  }

  private resetForm(): void {
    this.documentForm.reset();
    this.selectedFiles = [];
    this.formSubmitted = false;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_vehicle_upload_documents');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Document listing and preview methods
  isImageFile(url: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const extension = this.getFileExtension(url).toLowerCase();
    return imageExtensions.includes('.' + extension);
  }

  getFileExtension(url: string): string {
    const parts = url.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  previewDocument(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  deleteDocument(docType: string, urlIndex: number): void {
    // Check permission before deleting
    if (!this.canViewDocumentType(docType)) {
      console.error('Permission denied: Cannot delete this document type');
      return;
    }

    if (confirm('Are you sure you want to delete this document?')) {
      // Find the document type in the array
      const docIndex = this.selectedVehicle.docs.findIndex((doc: any) => doc.type === docType);

      if (docIndex !== -1) {
        // Remove the specific URL from the document type
        this.selectedVehicle.docs[docIndex].urls.splice(urlIndex, 1);

        // If no URLs left for this document type, remove the entire document type
        if (this.selectedVehicle.docs[docIndex].urls.length === 0) {
          this.selectedVehicle.docs.splice(docIndex, 1);
        }

        // Update vehicle using API
        const updateData = {
          docs: this.selectedVehicle.docs
        };

        this.subscriptions.push(
          this._vehicleService.UpdateItems(this.selectedVehicle._id, updateData).subscribe({
            next: (response: any) => {
              if (response.status) {
                console.log('Document deleted successfully');
                // Emit event to update vehicle listing
                this.documentsUploaded.emit();
              } else {
                console.error('Failed to delete document:', response.message);
              }
            },
            error: (error) => {
              console.error('Error deleting document:', error);
            }
          })
        );
      }
    }
  }

  // Helper methods for template
  getDocumentTypes(): any[] {
    if (!this.selectedVehicle?.docs || !Array.isArray(this.selectedVehicle.docs)) {
      return [];
    }

    // Filter documents based on user permissions
    return this.selectedVehicle.docs.filter((doc: any) => {
      return this.canViewDocumentType(doc.type);
    });
  }

  getDocumentTypesCount(): number {
    if (!this.selectedVehicle?.docs || !Array.isArray(this.selectedVehicle.docs)) {
      return 0;
    }
    return this.selectedVehicle.docs.length;
  }
}
